/**
 * Feedback Container Component
 *
 * Main container component managing the inline feedback flow:
 * - Click any button → Reset session, save old chatId, API call with saved chatId + empty message
 * - User changes mind, clicks different button → API call with saved chatId + empty message
 * - User submits form → API call with saved chatId + feedback text
 * - User sends new message → Uses new session ID
 * - Displays submitted feedback inline after submission
 */

import { type FC, useCallback, useEffect } from 'react';
import { useMessage } from '@assistant-ui/react';
import { FeedbackButton } from './FeedbackButton';
import { FeedbackForm } from './FeedbackForm';
import { FeedbackDisplay } from './FeedbackDisplay';
import { useSubmitFeedbackMutation } from '../../../store/api/feedbackApi';
import { SessionManager } from '../../../utils/sessionManager';
import { useFeedback } from '../../../hooks/useFeedback';
import type { FeedbackProps, FeedbackType } from '../../../types/feedback';
import { FEEDBACK_RESULT_VALUES, FEEDBACK_TEXT } from '../../../types/feedback';

/**
 * Feedback container component
 * Manages complete feedback flow using message metadata instead of component state
 */
export const Feedback: FC<FeedbackProps> = ({
  messageId,
  disabled = false,
}) => {
  const message = useMessage();

  const [submitFeedback, { isLoading: isSubmitting }] =
    useSubmitFeedbackMutation();

  const {
    startFeedback,
    submitFeedbackSuccess,
    resetFeedback,
    setFeedbackText,
    isActive,
    feedbackType: selectedFeedback,
    messageId: activeFeedbackMessageId,
    showForm,
    isSubmitted,
    feedbackChatId,
    feedbackText: reduxFeedbackText,
  } = useFeedback();

  // Get feedback state from message metadata
  const messageMetadataFeedback =
    message?.metadata?.custom?.feedbackState?.selected;
  const messageMetadataFeedbackCompleted =
    message?.metadata?.custom?.feedbackState?.completed;
  const messageMetadataFeedbackText =
    message?.metadata?.custom?.feedbackState?.feedbackText;
  const messageMetadataSessionId = message?.metadata?.custom?.sessionId;
  const isThisMessageActive = isActive && activeFeedbackMessageId === messageId;
  const selectedFeedbackForThisMessage =
    messageMetadataFeedback || (isThisMessageActive ? selectedFeedback : null);

  // Determine if we should show the form or display (for BOTH button types)
  // Only show form if this message is the last message
  const shouldShowForm =
    isThisMessageActive && showForm && !isSubmitted && message.isLast;

  const shouldShowDisplay =
    (isSubmitted && isThisMessageActive) ||
    (messageMetadataFeedbackText && messageMetadataFeedback !== null);

  // Use Redux feedback text for immediate display, fall back to metadata for persistence
  const displayFeedbackText =
    isSubmitted && isThisMessageActive
      ? reduxFeedbackText
      : messageMetadataFeedbackText;

  /**
   * Effect: Restore feedback form state after page reload
   * Checks sessionStorage for incomplete feedback and restores Redux state
   */
  useEffect(() => {
    // Only run once on mount, and only if not already active
    if (isActive) {
      return;
    }

    // Check if there's a saved feedback form state in sessionStorage
    const savedFormState = SessionManager.loadFeedbackFormState();

    if (!savedFormState) {
      return;
    }

    // Check if the saved state is for this message
    if (savedFormState.messageId !== messageId) {
      return;
    }

    // Check if message metadata shows incomplete feedback (completed = false or undefined)
    const messageMetadataCompleted =
      message?.metadata?.custom?.feedbackState?.completed;
    if (messageMetadataCompleted === true) {
      // Feedback already completed, clear the saved form state
      SessionManager.clearFeedbackFormState();
      return;
    }

    // Restore the feedback form state
    startFeedback(
      savedFormState.messageId,
      savedFormState.feedbackType,
      savedFormState.feedbackChatId,
    );
  }, []); // Run once on mount

  /**
   * Effect: Reset feedback state when this message is no longer the last message
   * This ensures clicking feedback on a new message works after the old form is hidden
   */
  useEffect(() => {
    // If feedback is active for this message but this message is no longer last, reset
    if (isThisMessageActive && !message.isLast) {
      resetFeedback();
      SessionManager.clearFeedbackChatId();
      SessionManager.clearFeedbackFormState();
    }
  }, [isThisMessageActive, message.isLast, resetFeedback]);

  /**
   * Handler for feedback button click
   * New flow: Save old chatId (on first click), reset session (on first click), API call with saved chatId + empty message
   */
  const handleFeedbackSelection = useCallback(
    async (feedbackType: FeedbackType) => {
      // Prevent interaction if disabled or submitting
      if (disabled || isSubmitting) {
        return;
      }

      // Don't allow feedback if this message already has COMPLETED feedback
      if (messageMetadataFeedbackCompleted === true) {
        return;
      }

      // Prevent feedback if there's already active feedback for a DIFFERENT message
      // UNLESS the current feedback has already been submitted (allow new feedback after submission)
      if (isActive && activeFeedbackMessageId !== messageId && !isSubmitted) {
        return;
      }

      // Allow switching feedback types - user can change their mind
      // User can click different buttons to update their feedback type

      // Determine the chatId to use for feedback
      let chatIdForFeedback: string;

      // If this is the first feedback interaction for this message
      if (!isThisMessageActive) {
        // Check if feedback was in progress before reload (metadata has sessionId)
        if (
          messageMetadataSessionId &&
          messageMetadataFeedback &&
          !messageMetadataFeedbackCompleted
        ) {
          // Restore feedback flow after page reload
          // Use the sessionId that was saved before reload
          chatIdForFeedback = messageMetadataSessionId;

          // Restore to sessionStorage so handleFormSubmit can find it
          SessionManager.saveFeedbackChatId(messageMetadataSessionId);
        } else {
          // First time feedback interaction - get current session ID
          const currentSessionId = SessionManager.getCurrentSessionId();
          if (!currentSessionId) {
            console.error('No session ID available');
            return;
          }

          // STEP 1: Save current session ID for feedback submission
          SessionManager.saveFeedbackChatId(currentSessionId);
          chatIdForFeedback = currentSessionId;

          // STEP 2: Clear session (set to null - new session created when user sends message)
          SessionManager.clearSession();
        }
      } else {
        // User is changing their mind - use already saved chatId
        const savedChatId =
          feedbackChatId || SessionManager.getFeedbackChatId();
        if (!savedChatId) {
          console.error('No saved feedback chat ID available');
          return;
        }
        chatIdForFeedback = savedChatId;
      }

      try {
        // STEP 3: API call with saved chatId + empty message
        await submitFeedback({
          chatId: chatIdForFeedback,
          message: '', // Empty message on button click
          result: feedbackType,
        }).unwrap();

        // STEP 4: Store feedback selection in message metadata (not completed yet)
        try {
          const currentMetadata = message.metadata || {};
          const currentCustom = (currentMetadata as any).custom || {};
          const updatedMetadata = {
            ...currentMetadata,
            custom: {
              ...currentCustom,
              sessionId: chatIdForFeedback,
              feedbackState: {
                selected: feedbackType,
                completed: false, // Not completed until form submitted
                messageId: messageId,
              },
            },
          };
          Object.assign(message.metadata, updatedMetadata);
        } catch (error) {
          console.error('Failed to update metadata:', error);
        }

        // STEP 5: Show inline form and update Redux state
        startFeedback(messageId, feedbackType, chatIdForFeedback);

        // STEP 6: Save form state to sessionStorage for page reload recovery
        SessionManager.saveFeedbackFormState(
          messageId,
          feedbackType,
          chatIdForFeedback,
        );
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },
    [
      disabled,
      isSubmitting,
      messageMetadataFeedbackCompleted,
      messageMetadataSessionId,
      messageMetadataFeedback,
      isActive,
      activeFeedbackMessageId,
      messageId,
      isThisMessageActive,
      feedbackChatId,
      submitFeedback,
      startFeedback,
      message,
      isSubmitted,
    ],
  );

  /**
   * Handler for inline feedback form submission
   * API call with saved chatId and feedback text
   */
  const handleFormSubmit = useCallback(
    async (text: string) => {
      // Use saved feedback chatId (old session before reset)
      const savedFeedbackChatId =
        feedbackChatId || SessionManager.getFeedbackChatId();
      if (!savedFeedbackChatId) {
        throw new Error('No feedback chat ID available');
      }

      if (!selectedFeedback) {
        throw new Error('No feedback type selected');
      }

      try {
        // STEP 1: API call with saved chatId and user's feedback text
        await submitFeedback({
          chatId: savedFeedbackChatId,
          message: text,
          result: selectedFeedback,
        }).unwrap();

        // STEP 2: Store feedback text in Redux for immediate display
        setFeedbackText(text);

        // STEP 3: Mark as submitted in Redux
        submitFeedbackSuccess();

        // STEP 4: Update message metadata with feedback text
        try {
          const currentMetadata = message.metadata || {};
          const currentCustom = (currentMetadata as any).custom || {};
          const updatedMetadata = {
            ...currentMetadata,
            custom: {
              ...currentCustom,
              sessionId: savedFeedbackChatId,
              feedbackState: {
                selected: selectedFeedback,
                completed: true,
                messageId: messageId,
                feedbackText: text,
                submittedAt: Date.now(),
              },
            },
          };
          Object.assign(message.metadata, updatedMetadata);
        } catch (error) {
          console.error('Failed to update metadata:', error);
        }

        // STEP 5: Clear saved feedback chatId
        SessionManager.clearFeedbackChatId();

        // STEP 6: Clear saved feedback form state
        SessionManager.clearFeedbackFormState();

        // Note: We don't call resetFeedback() here to keep isSubmitted=true
        // so the FeedbackDisplay shows immediately. When user starts feedback
        // on a new message, startFeedback() will reset the state.
      } catch (error) {
        console.error('Failed to submit feedback text:', error);
        throw error;
      }
    },
    [
      feedbackChatId,
      selectedFeedback,
      submitFeedback,
      setFeedbackText,
      submitFeedbackSuccess,
      message,
      messageId,
    ],
  );

  if (!messageId) {
    return null;
  }

  return (
    <div className='feedback-buttons-container w-full'>
      <div className='flex items-center gap-2'>
        <FeedbackButton
          type={FEEDBACK_RESULT_VALUES.SOLVED}
          isSelected={
            selectedFeedbackForThisMessage === FEEDBACK_RESULT_VALUES.SOLVED
          }
          isDisabled={disabled || messageMetadataFeedbackCompleted === true}
          isLoading={
            isSubmitting &&
            selectedFeedbackForThisMessage === FEEDBACK_RESULT_VALUES.SOLVED &&
            !isSubmitted
          }
          onClick={() => handleFeedbackSelection(FEEDBACK_RESULT_VALUES.SOLVED)}
        >
          {FEEDBACK_TEXT[FEEDBACK_RESULT_VALUES.SOLVED]}
        </FeedbackButton>

        <FeedbackButton
          type={FEEDBACK_RESULT_VALUES.NOT_HELPFUL}
          isSelected={
            selectedFeedbackForThisMessage ===
            FEEDBACK_RESULT_VALUES.NOT_HELPFUL
          }
          isDisabled={disabled || messageMetadataFeedbackCompleted === true}
          isLoading={
            isSubmitting &&
            selectedFeedbackForThisMessage ===
              FEEDBACK_RESULT_VALUES.NOT_HELPFUL &&
            !isSubmitted
          }
          onClick={() =>
            handleFeedbackSelection(FEEDBACK_RESULT_VALUES.NOT_HELPFUL)
          }
        >
          {FEEDBACK_TEXT[FEEDBACK_RESULT_VALUES.NOT_HELPFUL]}
        </FeedbackButton>
      </div>

      {/* Inline feedback form - shown after button click, before submission */}
      {shouldShowForm && selectedFeedback && (
        <FeedbackForm
          messageId={messageId}
          feedbackType={selectedFeedback}
          onSubmit={handleFormSubmit}
        />
      )}

      {/* Feedback display - shown after form submission */}
      {shouldShowDisplay && displayFeedbackText && (
        <FeedbackDisplay feedbackText={displayFeedbackText} />
      )}
    </div>
  );
};
